import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo/SeoHead";

const faqItems = [
  {
    q: "Is this only for Airbnb?",
    a: "Bellhopt supports short-term rentals across channels. Guests can order ahead of arrival regardless of where they booked.",
  },
  {
    q: "Do you stock inside the home?",
    a: "Yes. Delivery plus stocking is the point. We bring items in, stock the fridge and pantry, and organize so the home feels prepared.",
  },
  {
    q: "Does my team have to manage deliveries?",
    a: "No. Bellhopt handles fulfillment and coordination end to end.",
  },
  {
    q: "How do guests place orders?",
    a: "Guests browse over 20,000 products and order exactly what they want through a pre-arrival shopping experience sent before check-in.",
  },
  {
    q: "Can I control what the guest sees?",
    a: "Yes. The experience can be aligned to your standard and property expectations.",
  },
];

const GLASS_CARD =
  "rounded-3xl border border-[#E5E7EB] bg-white/95 backdrop-blur-2xl shadow-[0_24px_80px_rgba(15,23,42,0.12)]";
const GLASS_CARD_SOFT =
  "rounded-3xl border border-[#E5E7EB] bg-white/92 backdrop-blur-2xl shadow-[0_18px_60px_rgba(15,23,42,0.10)]";
const PRIMARY_CTA =
  "rounded-full px-7 py-3 text-white text-[12px] font-semibold tracking-[0.12em] uppercase bg-gradient-to-r from-[#FF4D4D] via-[#FF6B6B] to-[#FF4D4D] shadow-[0_14px_40px_rgba(248,113,113,0.45)] hover:shadow-[0_18px_54px_rgba(248,113,113,0.55)] transition-all";
const SECONDARY_CTA =
  "rounded-full px-7 py-3 text-[#111827] text-[12px] font-semibold tracking-[0.08em] uppercase border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] transition-all";

function Section({ id, title, children }) {
  return (
    <section id={id} className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
      <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">{title}</h2>
      <div className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">{children}</div>
    </section>
  );
}

function AirbnbGroceryDeliveryPage() {
  const title = "Airbnb Grocery Delivery for Vacation Rentals | Bellhopt";
  const description =
    "Give guests a pre-arrival grocery ordering link, then we deliver and stock before check-in. Bellhopt is the concierge layer for short-term rentals, PMS-connected and plug-and-play.";
  const canonical = "https://bellhopt.com/airbnb-grocery-delivery/";

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Airbnb Grocery Delivery, Delivered Like a Premium Arrival",
      url: canonical,
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://bellhopt.com/" },
        { "@type": "ListItem", position: 2, name: "Airbnb Grocery Delivery", item: canonical },
      ],
    },
  ];

  return (
    <div className="w-full min-h-screen bg-[#F9FAFB] pt-[72px] font-segoe">
      <SeoHead
        title={title}
        description={description}
        canonical="/airbnb-grocery-delivery/"
        robots="index,follow"
        ogTitle="Airbnb Grocery Delivery, Delivered Like a Premium Arrival"
        ogDescription="Guests order before check-in. Bellhopt delivers and stocks so every stay starts arrival-ready."
        structuredData={structuredData}
      />

      <main className="w-[90%] max-w-[1100px] m-auto py-10 mobile:w-[95%]">
        <section className={`${GLASS_CARD} p-8 mobile:p-5`}>
          <h1 className="font-segoe font-semibold text-[42px] mobile:text-[30px] text-[#111827] leading-tight">
            Airbnb Grocery Delivery, Delivered Like a Premium Arrival
          </h1>
          <p className="mt-4 text-[20px] mobile:text-[17px] text-[#6B7280] leading-8">
            Guests order before check-in. Bellhopt delivers and stocks so every stay starts arrival-ready, not last-minute.
          </p>
          <p className="mt-2 text-[14px] text-[#888888]">
            Integrated like software. Executed like concierge.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>
              Get A Demo
            </a>
            <Link to="/integrations" className={SECONDARY_CTA}>
              See Integrations
            </Link>
          </div>
        </section>

        <div className="mt-6 space-y-6">
          <Section id="what-this-is" title="Not Just Delivery. The Arrival Layer.">
            <p>Most grocery delivery ends at the door. Bellhopt finishes the job.</p>
            <p className="mt-4">
              We give your guests a simple pre-arrival ordering flow, then our concierge team handles delivery, access, stocking, and setup so the home feels prepared the moment they walk in.
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li>Guests arrive to a stocked, staged home</li>
              <li>Operators avoid shopping runs and vendor wrangling</li>
              <li>A premium standard that scales across the portfolio</li>
            </ul>
          </Section>

          <Section id="how-it-works" title="How Bellhopt Works">
            <ol className="list-decimal pl-6 space-y-4">
              <li>
                <strong>Pre-arrival ordering link tied to the stay</strong>
                <p>Guests browse 20,000+ products and order exactly what they want before check-in.</p>
              </li>
              <li>
                <strong>Concierge fulfillment and access</strong>
                <p>We shop, deliver, coordinate access, and bring items inside.</p>
              </li>
              <li>
                <strong>Stocking and setup</strong>
                <p>We stock the fridge and pantry so the home is arrival-ready.</p>
              </li>
            </ol>
          </Section>

          <Section id="what-guests-order" title="Everything Guests Expect, Before They Arrive">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
              {[
                "Fresh produce and groceries",
                "Coffee, snacks, breakfast items",
                "Family and baby products",
                "Health and wellness",
                "Celebration and special occasions",
                "Local favorites and premium upgrades",
              ].map((item) => (
                <div
                  key={item}
                  className="rounded-xl border border-[#E5E7EB] bg-white/90 p-4 text-[14px] text-[#111827] font-medium"
                >
                  {item}
                </div>
              ))}
            </div>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              A Better Arrival Without More Operations
            </h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {[
                { title: "Premium first impression", desc: "Arrivals feel intentional, consistent, and high standard." },
                { title: "Less team workload", desc: "No more last-minute store runs or coordinating handoffs." },
                { title: "Brand lift across every stay", desc: "Guests feel the difference immediately, even in mid-tier homes." },
                { title: "Scalable across portfolios", desc: "One workflow that works across one property or thousands." },
              ].map((card) => (
                <div key={card.title} className="rounded-2xl border border-[#E5E7EB] p-5 bg-white/90">
                  <h3 className="font-segoe font-semibold text-[18px] text-[#111827]">{card.title}</h3>
                  <p className="mt-2 text-[15px] text-[#6B7280] leading-relaxed">{card.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <Section id="who-for" title="Built for Hosts and Property Managers">
            <p>
              If you manage short-term rentals and want a more premium arrival experience without building a concierge team, Bellhopt is the operating layer you plug in and let run.
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li>Host looking to differentiate a premium listing</li>
              <li>Property manager standardizing arrivals across a portfolio</li>
              <li>Enterprise operator improving guest experience without added headcount</li>
            </ul>
          </Section>

          <Section id="faq" title="FAQ">
            <div className="space-y-3">
              {faqItems.map((item) => (
                <details key={item.q} className="rounded-2xl border border-[#E5E7EB] p-4 bg-white/90 open:bg-[#F9FAFB]">
                  <summary className="cursor-pointer font-segoe font-semibold text-[18px] text-[#111827]">{item.q}</summary>
                  <p className="mt-3 text-[16px] text-[#6B7280] leading-7">{item.a}</p>
                </details>
              ))}
            </div>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Turn Every Booking Into an Arrival-Ready Stay
            </h2>
            <p className="mt-3 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              Bellhopt is the concierge layer for short-term rentals. Plug in once and deliver a premium arrival standard at scale.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>Schedule A Demo</a>
              <a href="mailto:sales@bellhopt.com" className={SECONDARY_CTA}>Talk To Sales</a>
            </div>
            <p className="mt-6 text-[11px] text-[#9CA3AF]">
              Airbnb is a registered trademark of Airbnb, Inc. Bellhopt is not affiliated with Airbnb.
            </p>
          </section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">Related Pages</h2>
            <ul className="mt-4 list-disc pl-6 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              <li><Link className="text-primary" to="/airbnb-grocery-stocking">Airbnb grocery stocking</Link></li>
              <li><Link className="text-primary" to="/vacation-rental-grocery-delivery">Vacation rental grocery delivery</Link></li>
              <li><Link className="text-primary" to="/pre-arrival-groceries">Pre-arrival groceries</Link></li>
              <li><Link className="text-primary" to="/for/property-managers">For property managers</Link></li>
              <li><Link className="text-primary" to="/integrations">Integrations</Link></li>
              <li><Link className="text-primary" to="/how-it-works">How it works</Link></li>
            </ul>
          </section>
        </div>
      </main>
    </div>
  );
}

export default AirbnbGroceryDeliveryPage;
